import React, { useCallback, useEffect, useState } from 'react';
// import PropTypes from 'prop-types';
import Select from './Select';
import type {RCComboboxProps, SelectHandler, RCSelectOption, AmpmType} from './iRCTimePicker';

const formatOption = (option: number, disabledOptions: number[]) => {
    let value = `${option}`;
    if (option < 10) {
        value = `0${option}`;
    }

    let disabled = false;
    if (disabledOptions && disabledOptions.indexOf(option) >= 0) {
        disabled = true;
    }

    return {
        value,
        disabled
    };
};

const _MIN_COLUMN_WIDTH = 48 // UE标准：时分秒am列最小宽度

const Combobox: React.FC<RCComboboxProps> = (props) => {
    const {
        format,
        defaultOpenValue,
        prefixCls,
        value: propValue,
        onChange,
        onAmPmChange,
        showHour,
        showMinute,
        showSecond,
        hourOptions,
        minuteOptions,
        secondOptions,
        disabledHours,
        disabledMinutes,
        disabledSeconds,
        use12Hours,
        isAM,
        width,
        dir
    } = props;

    const [columnWidth, setColumnWidth] = useState(_MIN_COLUMN_WIDTH); // 新增宽度状态

    useEffect(() => {
        // 根据Input的宽度计算每一个列的基本宽度 added by: gx
        const columnNum = [showHour, showMinute, showSecond, use12Hours].filter(val => val).length;
        const baseWidth = width && parseInt((width / columnNum) + '') >= _MIN_COLUMN_WIDTH ? parseInt((width / columnNum) + '') : _MIN_COLUMN_WIDTH; // TODOS: 最后一列的宽度，用总宽度减去其他列的宽度计算,最小宽带应不小于_MIN_COLUMN_WIDTH_，否则会遮挡数字
        const baseMaxWidth = baseWidth < 48 ? baseWidth : 48; // TODOS: 最大宽度限制，防止日期框特别宽的情况下，面板特别大
        setColumnWidth(baseMaxWidth);
    }, [showHour, showMinute, showSecond, use12Hours, width]);

    const onItemChange: SelectHandler = useCallback((type, itemValue) => {
        const value = (propValue || defaultOpenValue).clone();

        if (type === 'hour') {
            if (use12Hours) {
                if (isAM) {
                    value.hour(+itemValue % 12);
                } else {
                    value.hour((+itemValue % 12) + 12);
                }
            } else {
                value.hour(+itemValue);
            }
        } else if (type === 'minute') {
            value.minute(+itemValue);
        } else if (type === 'ampm') {
            const ampm = itemValue.toUpperCase() as AmpmType;
            if (use12Hours) {
                if (ampm === 'PM' && value.hour() < 12) {
                    value.hour((value.hour() % 12) + 12);
                }

                if (ampm === 'AM') {
                    if (value.hour() >= 12) {
                        value.hour(value.hour() - 12);
                    }
                }
            }
            onAmPmChange?.(ampm);
        } else {
            value.second(+itemValue);
        }
        onChange(value);
    }, [propValue, defaultOpenValue, use12Hours, isAM, onChange, onAmPmChange]);

    const getHourSelect = useCallback((hour: number) => {
        if (!showHour) {
            return null;
        }
        const disabledOptions = disabledHours();
        let hourOptionsAdj;
        let hourAdj;
        if (use12Hours) {
            hourOptionsAdj = [12].concat(hourOptions.filter(h => h < 12 && h > 0));
            hourAdj = hour % 12 || 12;
        } else {
            hourOptionsAdj = hourOptions;
            hourAdj = hour;
        }

        return (
            <Select
                prefixCls={prefixCls}
                width={columnWidth} // 将计算出的宽度，赋予每一个列的Select组件 added by: gx
                options={hourOptionsAdj.map(option => formatOption(option, disabledOptions))}
                selectedIndex={hourOptionsAdj.indexOf(hourAdj)}
                type='hour'
                onSelect={onItemChange}
            />
        );
    }, [showHour, use12Hours, hourOptions, prefixCls, columnWidth, disabledHours, onItemChange]);

    const getMinuteSelect = useCallback((minute: number) => {
        if (!showMinute) {
            return null;
        }
        const value = propValue || defaultOpenValue;
        const disabledOptions = disabledMinutes(value.hour());

        return (
            <Select
                prefixCls={prefixCls}
                width={columnWidth}
                options={minuteOptions.map(option => formatOption(option, disabledOptions))}
                selectedIndex={minuteOptions.indexOf(minute)}
                type='minute'
                onSelect={onItemChange}
            />
        );
    }, [showMinute, propValue, defaultOpenValue, prefixCls, columnWidth, minuteOptions, disabledMinutes, onItemChange]);

    const getSecondSelect = useCallback((second: number) => {
        if (!showSecond) {
            return null;
        }
        const value = propValue || defaultOpenValue;
        const disabledOptions = disabledSeconds(value.hour(), value.minute());

        return (
            <Select
                prefixCls={prefixCls}
                width={columnWidth}
                options={secondOptions.map(option => formatOption(option, disabledOptions))}
                selectedIndex={secondOptions.indexOf(second)}
                type='second'
                onSelect={onItemChange}
            />
        );
    }, [showSecond, propValue, defaultOpenValue, prefixCls, columnWidth, secondOptions, disabledSeconds, onItemChange]);

    const getAMPMSelect = useCallback(() => {
        if (!use12Hours) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const AMPMOptions: RCSelectOption[] = ['am', 'pm']
            .map(c => (format.match(/\sA/) ? c.toUpperCase() : c))
            .map(c => ({value: c}));

        const selected = isAM ? 0 : 1;

        return (
            <Select
                prefixCls={prefixCls}
                width={columnWidth}
                options={AMPMOptions}
                selectedIndex={selected}
                type='ampm'
                onSelect={onItemChange}
            />
        );
    }, [use12Hours, format, isAM, prefixCls, columnWidth, onItemChange]);

    const value = propValue || defaultOpenValue;
    return (
        <div className={`${prefixCls}-combobox ${dir === 'rtl' ? `${prefixCls}-combobox-rtl` : ""}`}>
            {getHourSelect(value.hour())}
            {getMinuteSelect(value.minute())}
            {getSecondSelect(value.second())}
            {getAMPMSelect()}
        </div>
    );
};

export default Combobox;